import { useState, type ReactNode } from 'react';
import {
  CREDENTIAL_LABELS,
  ENVIRONMENT_IDS,
  type CredentialKind,
  type CredentialMetadata,
  type EnvironmentId,
} from '@sfpbxtestbot/contracts';
import { CSP_DIRECTIVES } from '@sfpbxtestbot/env-matrix';
import { api } from '../lib/api.js';
import { GOOGLE_VOICE_HINT, SalesforceLoginFrame } from './SalesforceLoginWindow.js';
import { useAsync } from '../lib/useAsync.js';
import {
  Button,
  Callout,
  Card,
  Field,
  PageHeader,
  StatusBadge,
  formatRelative,
} from '../components/primitives.js';

interface CredentialSpec {
  kind: CredentialKind;
  what: string;
  where: string;
  perEnvironment: boolean;
  multiline?: boolean;
  /** Some credentials are two values that must be stored together. */
  pair?: { idLabel: string; secretLabel: string };
  /** Several named values stored together as one JSON object. */
  fields?: CredentialField[];
}

interface CredentialField {
  key: string;
  label: string;
  secret?: boolean;
  optional?: boolean;
  placeholder?: string;
  hint?: string;
}

const CUSTOMER_SIP_FIELDS: CredentialField[] = [
  { key: 'username', label: 'SIP username', placeholder: '8502' },
  { key: 'password', label: 'SIP password', secret: true },
  {
    key: 'domain',
    label: 'SIP domain',
    placeholder: 'node2.wss.registrar.rp4s.ai',
    hint: 'The realm the extension registers against.',
  },
  {
    key: 'websocketUrl',
    label: 'WebSocket URL',
    placeholder: 'wss://node2.wss.registrar.rp4s.ai:443',
    hint: 'The secure WebSocket the registrar listens on.',
  },
  {
    key: 'phoneNumber',
    label: 'Phone number (DID)',
    optional: true,
    placeholder: '+44 20 7946 0001',
    hint: 'The number the agent dials to reach this phone. Used when the run form leaves the number to dial empty.',
  },
];

const SPECS: CredentialSpec[] = [
  {
    kind: 'gateway-client',
    what: 'Lets the platform call the gateway API exactly the way the Salesforce package does.',
    where:
      'The same client id and secret you put on the RocketPhone Auth external credential principal.',
    perEnvironment: true,
    pair: { idLabel: 'Client ID', secretLabel: 'Client secret' },
  },
  {
    kind: 'admin-api-key',
    what: 'Lets the platform check the provisioning API: tenants, extensions, live calls and call records.',
    where: 'Issued per tenant by the PBX platform team.',
    perEnvironment: true,
  },
  {
    kind: 'portal-jwt',
    what: 'Lets the platform read the tenant bookmarks and phone numbers that drive the live call.',
    where:
      'Sign in to the Admin Portal with your work Google account, then copy the token from the browser session.',
    perEnvironment: false,
  },
  {
    kind: 'salesforce-session',
    what: 'Lets the bot drive Salesforce Setup as you, without automating a login through multi-factor authentication.',
    where:
      'Click Sign in to Salesforce. A browser window opens on the org login page; finish MFA there and this page saves the session.',
    perEnvironment: true,
    multiline: true,
  },
  {
    kind: 'google-service-account',
    what: 'Renders the customer and agent lines of the test call as natural speech.',
    where:
      'A Gemini API key from Google AI Studio (aistudio.google.com/apikey), created with your work Google account. Paste the key itself, not a service-account file.',
    perEnvironment: false,
    multiline: true,
  },
  {
    kind: 'customer-sip',
    what: 'Optional. Only if you already have SIP login details for a phone on the tenant.',
    where:
      'Leave this empty. The run reads the existing QA tenant, adds one new customer phone on it, and deletes only that phone when the call ends.',
    perEnvironment: true,
    fields: CUSTOMER_SIP_FIELDS,
  },
];

/** Settings: credentials in, nothing back out, plus the environment reference. */
export function SettingsPage(): ReactNode {
  const credentials = useAsync(() => api.credentials(), []);
  const environments = useAsync(() => api.environments(), []);
  const [notice, setNotice] = useState<string>();

  const stored = new Map<string, CredentialMetadata>();
  for (const entry of credentials.data?.credentials ?? []) {
    if (entry.present) stored.set(`${entry.kind}:${entry.environment ?? '*'}`, entry);
  }

  return (
    <div className="stack">
      <PageHeader
        title="Settings"
        lede="Credentials are encrypted before they are stored and are never shown again. The platform only decrypts them when a run needs them."
      />

      {notice ? <Callout tone="info">{notice}</Callout> : null}

      <Card title="Credentials">
        <div className="stack">
          <GoogleVoiceSessionEditor
            stored={stored}
            onSaved={(message) => {
              setNotice(message);
              credentials.reload();
            }}
          />
          {SPECS.map((spec) =>
            spec.kind === 'salesforce-session' ? (
              <SalesforceSessionEditor
                key={spec.kind}
                spec={spec}
                stored={stored}
                onSaved={(message) => {
                  setNotice(message);
                  credentials.reload();
                }}
              />
            ) : (
              <CredentialEditor
                key={spec.kind}
                spec={spec}
                stored={stored}
                onSaved={(message) => {
                  setNotice(message);
                  credentials.reload();
                }}
              />
            ),
          )}
        </div>
      </Card>

      <Card
        title="Environment matrix"
        subtitle="The single source of truth for hosts, Remote Site Settings and Trusted URLs. Configuration steps and their checks both read from this, so they cannot disagree."
      >
        {(environments.data?.environments ?? []).map((environment) => (
          <details key={environment.id} className="disclosure" style={{ paddingTop: 12 }}>
            <summary>
              {environment.label} · {environment.gatewayUrl}
            </summary>
            <div className="stack stack--tight" style={{ marginTop: 12, fontSize: 13.5 }}>
              <div className="grid grid--2">
                <div>
                  <strong>Gateway</strong>
                  <div className="mono">{environment.gatewayUrl}</div>
                </div>
                <div>
                  <strong>Softphone app</strong>
                  <div className="mono">{environment.appUrl}</div>
                </div>
                <div>
                  <strong>Token endpoint</strong>
                  <div className="mono">{environment.tokenUrl}</div>
                </div>
                <div>
                  <strong>Portal environment code</strong>
                  <div className="mono">{environment.portalEnvCode}</div>
                </div>
              </div>

              <div>
                <strong>Remote Site Settings</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {environment.remoteSites.map((site) => (
                    <li key={site.name} className="mono">
                      {site.url}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <strong>Trusted URLs</strong>
                <span className="muted">
                  {' '}
                  — all {CSP_DIRECTIVES.length} CSP directives are ticked on each
                </span>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {environment.trustedUrls.map((url) => (
                    <li key={url.name} className="mono">
                      {url.url}
                    </li>
                  ))}
                </ul>
              </div>

              {environment.adminApiUrl ? null : (
                <Callout tone="warning">
                  This environment has no published provisioning API address, so those checks report
                  as not configured. Set SFPBX_ADMIN_API_URL_{environment.id.toUpperCase()} to enable
                  them.
                </Callout>
              )}
            </div>
          </details>
        ))}
      </Card>
    </div>
  );
}

function SalesforceSessionEditor({
  spec,
  stored,
  onSaved,
}: {
  spec: CredentialSpec;
  stored: Map<string, CredentialMetadata>;
  onSaved: (message: string) => void;
}): ReactNode {
  const [environment, setEnvironment] = useState<EnvironmentId | null>('qa');
  const [orgUrl, setOrgUrl] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [embedded, setEmbedded] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState('');
  const [saving, setSaving] = useState(false);

  const existing = stored.get(`salesforce-session:${environment ?? '*'}`);
  const expiringSoon =
    existing?.expiresAt != null &&
    new Date(existing.expiresAt).getTime() - Date.now() < 3 * 24 * 60 * 60 * 1_000;

  async function connect(): Promise<void> {
    const popup = window.open(
      'about:blank',
      'sfpbx-salesforce-login',
      'popup=yes,width=1140,height=820',
    );
    setConnecting(true);
    setEmbedded(false);
    setError(undefined);
    setMessage(undefined);
    try {
      const started = await api.startSalesforceConnect({
        salesforceOrgUrl: orgUrl.trim(),
        environment,
      });
      if (popup && !popup.closed) {
        popup.location.href = '/salesforce-login';
        setMessage('A login window opened. Finish signing in there, including any MFA prompt.');
      } else {
        popup?.close();
        setEmbedded(true);
        setMessage(started.message ?? 'Allow pop-ups to open the Salesforce login in its own window.');
      }
      await waitForLogin();
    } catch (cause) {
      popup?.close();
      setEmbedded(false);
      setError(cause instanceof Error ? cause.message : 'Salesforce sign-in could not start.');
      setConnecting(false);
    }
  }

  async function waitForLogin(): Promise<void> {
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, 1_000));
      const status = await api.salesforceConnectStatus();
      if (status.status === 'waiting') {
        setMessage(status.message);
        continue;
      }
      setConnecting(false);
      if (status.status === 'saved') {
        onSaved(status.message ?? 'Salesforce session saved.');
        return;
      }
      setError(status.message ?? 'Salesforce sign-in did not finish.');
      return;
    }
  }

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
      <div className="row row--between" style={{ marginBottom: 8 }}>
        <div className="stack stack--tight" style={{ gap: 2 }}>
          <strong>{CREDENTIAL_LABELS[spec.kind]}</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            {spec.what}
          </span>
        </div>
        <StatusBadge
          outcome={existing ? (expiringSoon ? 'warning' : 'passed') : 'pending'}
          label={existing ? (expiringSoon ? 'Expiring soon' : 'Saved') : 'Not set'}
        />
      </div>

      <p className="muted" style={{ fontSize: 12.5 }}>
        A login window opens on the org's own login page. Salesforce asks for the username first;
        press Enter, and the password field appears on the next page.
      </p>

      {existing ? (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
          Saved {formatRelative(existing.updatedAt)}
          {existing.hint ? ` · ${existing.hint}` : ''}
          {existing.expiresAt ? ` · expires ${formatRelative(existing.expiresAt)}` : ''}
        </p>
      ) : null}

      <div className="stack stack--tight" style={{ marginTop: 12 }}>
        <Field label="Applies to">
          <select
            className="select"
            value={environment ?? ''}
            onChange={(event) => setEnvironment((event.target.value || null) as EnvironmentId | null)}
          >
            <option value="">Every environment</option>
            {ENVIRONMENT_IDS.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Salesforce org URL"
          hint="For example https://acme.my.salesforce.com"
          error={error}
        >
          <input
            className="input"
            value={orgUrl}
            placeholder="https://acme.my.salesforce.com"
            onChange={(event) => setOrgUrl(event.target.value)}
          />
        </Field>
        {message && connecting ? <Callout tone="info">{message}</Callout> : null}
        {connecting && embedded ? <SalesforceLoginFrame /> : null}
        <div className="row">
          <Button
            variant="primary"
            size="small"
            busy={connecting}
            disabled={!orgUrl.trim()}
            onClick={() => void connect()}
          >
            {existing ? 'Sign in again' : 'Sign in to Salesforce'}
          </Button>
          {connecting ? (
            <Button size="small" onClick={() => void api.cancelSalesforceConnect()}>
              Cancel
            </Button>
          ) : null}
        </div>
        <details
          className="disclosure"
          open={pasteOpen}
          onToggle={(event) => setPasteOpen(event.currentTarget.open)}
        >
          <summary>Paste a session file instead</summary>
          <div className="stack stack--tight" style={{ marginTop: 12 }}>
            <textarea
              className="textarea"
              value={paste}
              placeholder="Paste the whole Playwright storageState JSON"
              onChange={(event) => setPaste(event.target.value)}
            />
            <Button
              variant="primary"
              size="small"
              busy={saving}
              disabled={!paste.trim()}
              onClick={() => {
                setSaving(true);
                void api
                  .saveCredential({ kind: 'salesforce-session', environment, value: paste.trim() })
                  .then(() => {
                    setPaste('');
                    setPasteOpen(false);
                    onSaved('Salesforce session saved.');
                  })
                  .catch((cause: unknown) => {
                    setError(cause instanceof Error ? cause.message : 'That could not be saved.');
                  })
                  .finally(() => setSaving(false));
              }}
            >
              Save pasted file
            </Button>
          </div>
        </details>
      </div>
    </div>
  );
}

/** One Google sign-in, shared by every environment. */
function GoogleVoiceSessionEditor({
  stored,
  onSaved,
}: {
  stored: Map<string, CredentialMetadata>;
  onSaved: (message: string) => void;
}): ReactNode {
  const [connecting, setConnecting] = useState(false);
  const [embedded, setEmbedded] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const existing = stored.get('google-voice-session:*');

  async function connect(): Promise<void> {
    const popup = window.open('about:blank', 'sfpbx-salesforce-login', 'popup=yes,width=1140,height=820');
    setConnecting(true);
    setEmbedded(false);
    setError(undefined);
    setMessage(undefined);
    try {
      const started = await api.startGoogleVoiceConnect();
      if (popup && !popup.closed) {
        popup.location.href = '/salesforce-login';
      } else {
        popup?.close();
        setEmbedded(true);
      }
      setMessage(started.message);
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, 1_000));
        const status = await api.salesforceConnectStatus();
        if (status.status === 'waiting') continue;
        setConnecting(false);
        if (status.status === 'saved') onSaved(status.message ?? 'Google Voice session saved.');
        else setError(status.message ?? 'Google Voice sign-in did not finish.');
        return;
      }
    } catch (cause) {
      popup?.close();
      setEmbedded(false);
      setConnecting(false);
      setError(cause instanceof Error ? cause.message : 'Google Voice sign-in could not start.');
    }
  }

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
      <div className="row row--between" style={{ marginBottom: 8 }}>
        <div className="stack stack--tight" style={{ gap: 2 }}>
          <strong>{CREDENTIAL_LABELS['google-voice-session']}</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            Optional. Lets a Google Voice number play the customer, so the test call goes over the real
            phone network.
          </span>
        </div>
        <StatusBadge outcome={existing ? 'passed' : 'pending'} label={existing ? 'Saved' : 'Not set'} />
      </div>
      <p className="muted" style={{ fontSize: 12.5 }}>
        Use a Google account kept just for testing. Before signing in, open voice.google.com in your own
        browser with that account and, under Settings → Calls, make sure calls can be made and received on
        the web.
      </p>
      {existing ? (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
          Saved {formatRelative(existing.updatedAt)}
          {existing.hint ? ` · ${existing.hint}` : ''}
        </p>
      ) : null}
      <div className="stack stack--tight" style={{ marginTop: 12 }}>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        {message && connecting ? <Callout tone="info">{message}</Callout> : null}
        {connecting && embedded ? <SalesforceLoginFrame hint={GOOGLE_VOICE_HINT} /> : null}
        <div className="row">
          <Button variant="primary" size="small" busy={connecting} onClick={() => void connect()}>
            {existing ? 'Sign in again' : 'Sign in to Google Voice'}
          </Button>
          {connecting ? (
            <Button size="small" onClick={() => void api.cancelSalesforceConnect()}>
              Cancel
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CredentialEditor({
  spec,
  stored,
  onSaved,
}: {
  spec: CredentialSpec;
  stored: Map<string, CredentialMetadata>;
  onSaved: (message: string) => void;
}): ReactNode {
  const [environment, setEnvironment] = useState<EnvironmentId | null>(
    spec.perEnvironment ? 'qa' : null,
  );
  const [value, setValue] = useState('');
  const [secondary, setSecondary] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const existing = stored.get(`${spec.kind}:${environment ?? '*'}`);
  const fieldsComplete =
    spec.fields?.every((field) => field.optional || Boolean(fieldValues[field.key]?.trim())) ?? true;

  async function save(): Promise<void> {
    setSaving(true);
    setError(undefined);
    try {
      let payload: string;
      let hint: string | undefined;
      if (spec.fields) {
        const object: Record<string, string> = {};
        for (const field of spec.fields) {
          const entry = field.secret ? fieldValues[field.key] ?? '' : (fieldValues[field.key] ?? '').trim();
          if (entry) object[field.key] = entry;
        }
        payload = JSON.stringify(object);
        hint = [object.username, object.domain].filter(Boolean).join('@') || undefined;
      } else if (spec.pair) {
        payload = JSON.stringify({ clientId: value.trim(), clientSecret: secondary });
        hint = `client ${value.trim().slice(0, 12)}…`;
      } else {
        payload = value.trim();
      }

      await api.saveCredential({ kind: spec.kind, environment, value: payload, hint });
      setValue('');
      setSecondary('');
      setFieldValues({});
      setEditing(false);
      onSaved(`${CREDENTIAL_LABELS[spec.kind]} saved.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const expiringSoon =
    existing?.expiresAt != null &&
    new Date(existing.expiresAt).getTime() - Date.now() < 3 * 24 * 60 * 60 * 1_000;

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 16 }}>
      <div className="row row--between" style={{ marginBottom: 8 }}>
        <div className="stack stack--tight" style={{ gap: 2 }}>
          <strong>{CREDENTIAL_LABELS[spec.kind]}</strong>
          <span className="muted" style={{ fontSize: 13 }}>
            {spec.what}
          </span>
        </div>
        <div className="row">
          <StatusBadge
            outcome={existing ? (expiringSoon ? 'warning' : 'passed') : 'pending'}
            label={existing ? (expiringSoon ? 'Expiring soon' : 'Saved') : 'Not set'}
          />
          <Button size="small" onClick={() => setEditing((open) => !open)}>
            {editing ? 'Cancel' : existing ? 'Replace' : 'Add'}
          </Button>
        </div>
      </div>

      <p className="muted" style={{ fontSize: 12.5 }}>
        Where to get it: {spec.where}
      </p>

      {existing ? (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
          Saved {formatRelative(existing.updatedAt)}
          {existing.hint ? ` · ${existing.hint}` : ''}
          {existing.expiresAt ? ` · expires ${formatRelative(existing.expiresAt)}` : ''}
        </p>
      ) : null}

      {editing ? (
        <div className="stack stack--tight" style={{ marginTop: 12 }}>
          {spec.perEnvironment ? (
            <Field label="Applies to">
              <select
                className="select"
                value={environment ?? ''}
                onChange={(event) =>
                  setEnvironment((event.target.value || null) as EnvironmentId | null)
                }
              >
                <option value="">Every environment</option>
                {ENVIRONMENT_IDS.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {spec.fields ? (
            spec.fields.map((field, index) => (
              <Field
                key={field.key}
                label={field.optional ? `${field.label} (optional)` : field.label}
                hint={field.hint}
                error={index === 0 ? error : undefined}
              >
                <input
                  className="input"
                  type={field.secret ? 'password' : 'text'}
                  value={fieldValues[field.key] ?? ''}
                  placeholder={field.placeholder}
                  autoComplete={field.secret ? 'new-password' : 'off'}
                  onChange={(event) =>
                    setFieldValues((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                />
              </Field>
            ))
          ) : (
            <Field label={spec.pair?.idLabel ?? 'Value'} error={error}>
              {spec.multiline ? (
                <textarea
                  className="textarea"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  placeholder={spec.kind === 'google-service-account' ? 'Paste the API key' : 'Paste the whole JSON file'}
                />
              ) : (
                <input
                  className="input"
                  value={value}
                  autoComplete="off"
                  onChange={(event) => setValue(event.target.value)}
                />
              )}
            </Field>
          )}

          {spec.pair ? (
            <Field label={spec.pair.secretLabel}>
              <input
                className="input"
                type="password"
                value={secondary}
                autoComplete="new-password"
                onChange={(event) => setSecondary(event.target.value)}
              />
            </Field>
          ) : null}

          <div className="row">
            <Button
              variant="primary"
              size="small"
              busy={saving}
              disabled={
                spec.fields ? !fieldsComplete : !value.trim() || (spec.pair != null && !secondary)
              }
              onClick={() => void save()}
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
